import type { FC, MouseEvent } from 'react';
import React, { memo } from 'react';
import cn from 'classnames';

import type { TTag } from './models';

const noop = () => {};

const textSizeMapped = {
    xs: 'component-secondary',
    s: 'component',
    m: 'component',
    l: 'primary-large',
} as const;

export const Tag: FC<TTag> = memo(
    ({
        Text,
        bordered,
        className,
        classNames,
        dataTestId,
        disabled,
        forwardedRef,
        label,
        onClick = noop,
        selected,
        size = 'm',
        styles,
        tabIndex,
        role,
        returnEvent,
        allOnclickArguments = false,
        value,
        inline,
        theme = 'white',
    }) => {
        const isSelected = value === selected;
        const tagClassNames = cn(
            styles.tag,
            styles.size,
            {
                [styles.selected]: isSelected,
                [styles.hovered]: !isSelected && !disabled,
                [styles.disabled]: disabled,
            },
            styles[`${size}Size`],
            {
                [styles.bordered]: bordered,
            },
            styles[theme],
            classNames,
            className
        );
        const captionClassNames = cn(styles.external, styles.text, {
            [styles.selectedText]: isSelected,
        });

        const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
            if (allOnclickArguments) {
                onClick(event, value);

                return;
            }
            onClick(returnEvent ? event : value);
        };

        return (
            <button
                ref={forwardedRef}
                className={tagClassNames}
                data-test-id={dataTestId}
                data-test-selected={isSelected || undefined}
                onClick={handleClick}
                role={role}
                tabIndex={tabIndex}
                type="button"
            >
                <Text
                    className={captionClassNames}
                    styler={{ margin: 0 }}
                    tag={inline ? 'span' : 'p'}
                    view={textSizeMapped[size]}
                >
                    {label}
                </Text>
            </button>
        );
    }
);
